import {
  classifyPublicationRoute,
  PUBLICATION_ROUTE_CLASSES,
} from './publicationRoutePolicy.js';

export const SELECTIVE_PRIMARY_NAV_IDS = Object.freeze({
  HOME: 'home',
  GAMES: 'games',
  DEAL_FINDER: 'deal-finder',
  NEWS: 'news',
  SUPPORT: 'support',
});

export const SELECTIVE_NAVIGATION_CLASSES = Object.freeze({
  PRIMARY: 'primary',
  CHILD: 'child',
  NEUTRAL: 'neutral',
  HIDDEN_PRODUCT: 'hidden_product',
  BACKSTAGE: 'backstage',
  UNKNOWN: 'unknown',
});

export const SELECTIVE_NAVIGATION_GO_LIVE_GATES = Object.freeze({
  DEAL_FINDER_5K: 'F2-5K',
});

const primaryItem = ({ id, href, labelKey, goLiveGate = null }) =>
  Object.freeze({
    id,
    href,
    labelKey,
    goLiveGate,
  });

export const SELECTIVE_PRIMARY_NAV_ITEMS = Object.freeze([
  primaryItem({
    id: SELECTIVE_PRIMARY_NAV_IDS.HOME,
    href: '/',
    labelKey: 'nav.home',
  }),
  primaryItem({
    id: SELECTIVE_PRIMARY_NAV_IDS.GAMES,
    href: '/games',
    labelKey: 'nav.games',
  }),
  primaryItem({
    id: SELECTIVE_PRIMARY_NAV_IDS.DEAL_FINDER,
    href: '/deal-finder',
    labelKey: 'nav.deal_finder',
    goLiveGate: SELECTIVE_NAVIGATION_GO_LIVE_GATES.DEAL_FINDER_5K,
  }),
  primaryItem({
    id: SELECTIVE_PRIMARY_NAV_IDS.NEWS,
    href: '/news',
    labelKey: 'nav.news',
  }),
  primaryItem({
    id: SELECTIVE_PRIMARY_NAV_IDS.SUPPORT,
    href: '/support',
    labelKey: 'nav.support',
  }),
]);

const PRIMARY_ITEM_BY_ID = new Map(
  SELECTIVE_PRIMARY_NAV_ITEMS.map((item) => [item.id, item]),
);

const PRIMARY_ID_BY_PATHNAME = new Map(
  SELECTIVE_PRIMARY_NAV_ITEMS.map((item) => [item.href, item.id]),
);

const BACKSTAGE_ROUTE_CLASSES = new Set([
  PUBLICATION_ROUTE_CLASSES.INTERNAL_ADMIN,
  PUBLICATION_ROUTE_CLASSES.STAFF_ACCESS,
  PUBLICATION_ROUTE_CLASSES.OPERATIONAL,
  PUBLICATION_ROUTE_CLASSES.PROVIDER_RUNTIME,
  PUBLICATION_ROUTE_CLASSES.DEV_TEST,
]);

const CHILD_ROUTE_CLASSES = new Set([
  PUBLICATION_ROUTE_CLASSES.PUBLIC,
  PUBLICATION_ROUTE_CLASSES.LEGACY,
  PUBLICATION_ROUTE_CLASSES.TOKEN_UTILITY,
]);

const resolveChildPrimaryId = (pathname, routeClass) => {
  if (!CHILD_ROUTE_CLASSES.has(routeClass)) {
    return null;
  }

  if (pathname === '/gamedetails' || pathname.startsWith('/games/')) {
    return SELECTIVE_PRIMARY_NAV_IDS.GAMES;
  }

  if (pathname.startsWith('/deal-finder/outbound/')) {
    return SELECTIVE_PRIMARY_NAV_IDS.DEAL_FINDER;
  }

  if (
    pathname === '/articledetails' ||
    pathname === '/news/archive' ||
    pathname.startsWith('/news/')
  ) {
    return SELECTIVE_PRIMARY_NAV_IDS.NEWS;
  }

  if (
    pathname === '/support/faq' ||
    pathname.startsWith('/support/faq/') ||
    pathname.startsWith('/support/feedback/')
  ) {
    return SELECTIVE_PRIMARY_NAV_IDS.SUPPORT;
  }

  return null;
};

const navigationResult = ({
  publicationRoute,
  navigationClass,
  activePrimaryId = null,
}) => ({
  pathname: publicationRoute.pathname,
  publicationRouteClass: publicationRoute.routeClass,
  publicationRouteId: publicationRoute.routeId,
  navigationClass,
  activePrimaryId,
  activeHref: activePrimaryId
    ? PRIMARY_ITEM_BY_ID.get(activePrimaryId)?.href ?? null
    : null,
});

export const resolveSelectiveNavigationRoute = (value) => {
  const publicationRoute = classifyPublicationRoute(value);
  const primaryId = PRIMARY_ID_BY_PATHNAME.get(publicationRoute.pathname);

  if (
    primaryId &&
    publicationRoute.routeClass === PUBLICATION_ROUTE_CLASSES.PUBLIC
  ) {
    return navigationResult({
      publicationRoute,
      navigationClass: SELECTIVE_NAVIGATION_CLASSES.PRIMARY,
      activePrimaryId: primaryId,
    });
  }

  const childPrimaryId = resolveChildPrimaryId(
    publicationRoute.pathname,
    publicationRoute.routeClass,
  );
  if (childPrimaryId) {
    return navigationResult({
      publicationRoute,
      navigationClass: SELECTIVE_NAVIGATION_CLASSES.CHILD,
      activePrimaryId: childPrimaryId,
    });
  }

  if (
    publicationRoute.routeClass === PUBLICATION_ROUTE_CLASSES.HIDDEN_PRODUCT
  ) {
    return navigationResult({
      publicationRoute,
      navigationClass: SELECTIVE_NAVIGATION_CLASSES.HIDDEN_PRODUCT,
    });
  }

  if (BACKSTAGE_ROUTE_CLASSES.has(publicationRoute.routeClass)) {
    return navigationResult({
      publicationRoute,
      navigationClass: SELECTIVE_NAVIGATION_CLASSES.BACKSTAGE,
    });
  }

  if (publicationRoute.routeClass === PUBLICATION_ROUTE_CLASSES.UNKNOWN) {
    return navigationResult({
      publicationRoute,
      navigationClass: SELECTIVE_NAVIGATION_CLASSES.UNKNOWN,
    });
  }

  return navigationResult({
    publicationRoute,
    navigationClass: SELECTIVE_NAVIGATION_CLASSES.NEUTRAL,
  });
};

export const isSelectivePrimaryNavigationItemActive = (itemOrId, value) => {
  const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
  return resolveSelectiveNavigationRoute(value).activePrimaryId === itemId;
};
